import React, { useState, useRef, useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Button, Icon } from '@/atoms'
import { Paragraph } from '@/atoms/Typography'

import * as S from './styles'

const IngegnoDot = ({
  id,
  x,
  y,
  data,
  isActive,
  setActiveTooltipId,
  animationsEnabled = true,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const dotRef = useRef(null)
  const tooltipRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (!isActive) {
      setIsHovered(false)
    }
  }, [isActive])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const determineTooltipPosition = () => {
    if (isMobile) return 'bottom'

    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : 1000
    const centerPointX = (viewportWidth * x) / 100

    return centerPointX < viewportWidth / 2 ? 'left' : 'right'
  }

  const tooltipPosition = determineTooltipPosition()

  const handleClick = () => {
    setActiveTooltipId(isActive ? null : id)

    if (!isMobile && !isActive) {
      setIsHovered(true)
    }
  }

  const handleMouseEnter = () => {
    // Cancella eventuali timeout di dismissione pendenti
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    if (!isMobile) {
      setIsHovered(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => {
        if (!isActive) {
          setIsHovered(false)
        }
      }, 100)
    }
  }

  const pulseVariants = {
    pulse: {
      scale: [1, 2, 1],
      opacity: [0.9, 0.7, 0.9],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: 'loop',
      },
    },
  }

  return (
    <>
      <S.DotContainer
        className='dot-container'
        ref={dotRef}
        style={{ left: `${x}%`, top: `${y}%` }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <S.PulseWrapper>
          <S.Pulse
            variants={animationsEnabled ? pulseVariants : {}}
            animate='pulse'
            style={{
              scale: 1.7,
            }}
          />
        </S.PulseWrapper>

        <S.Dot
          whileHover={animationsEnabled ? { scale: 1.1 } : {}}
          whileTap={animationsEnabled ? { scale: 0.95 } : {}}
          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        />
      </S.DotContainer>

      <AnimatePresence>
        {(isHovered || isActive) && (
          <S.TooltipWrapper
            style={{ left: `${x}%`, top: `${y}%` }}
            $isMobile={isMobile}
            $isActive={isActive}
          >
            <S.TooltipContainer
              className='tooltip-container'
              ref={tooltipRef}
              $position={tooltipPosition}
              $isMobile={isMobile}
              $isActive={isActive}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <S.TooltipHeader
                $isActive={isActive}
                onClick={handleClick}
              >
                <S.IconContainer>
                  {data.icon && (
                    <Icon
                      name={data.icon}
                      big
                    />
                  )}
                </S.IconContainer>
                <S.Title
                  typo='displayXS'
                  bold
                >
                  {data.title}
                </S.Title>
                {isActive ? (
                  <Icon
                    name={'chevron-up'}
                    big
                  />
                ) : (
                  <Icon
                    name={'chevron-down'}
                    big
                  />
                )}
              </S.TooltipHeader>

              <AnimatePresence>
                {isActive && (
                  <S.TooltipBody
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* {data.content} */}
                    <Paragraph
                      typo='paragraph1'
                      content={data.content}
                    />
                    {data.ctas && data.ctas.length > 0 && (
                      <S.TooltipBodyCtasContainer>
                        {data.ctas.map((cta, index) => (
                          <Button
                            key={`ingegno-cta-${index}`}
                            primary
                            {...cta}
                          >
                            {cta.label}
                          </Button>
                        ))}
                      </S.TooltipBodyCtasContainer>
                    )}
                  </S.TooltipBody>
                )}
              </AnimatePresence>
            </S.TooltipContainer>
          </S.TooltipWrapper>
        )}
      </AnimatePresence>
    </>
  )
}

export default IngegnoDot
